// src/pages/PendingActions/components/DNAModal.js
import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { AlertTriangle, UserX } from 'lucide-react';

const DNAModal = ({ show, onHide, patient, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleSubmit = async () => {
    if (!reason) {
      return; // reason is required; button will stay disabled until selected
    }
    
    if (!patient?.cycleId) {
      console.error('Patient cycleId is missing:', patient);
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm(patient.id, patient.cycleId, reason, notes);
      handleReset();
    } catch (error) {
      console.error('DNA confirmation failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setReason('');
    setNotes('');
  };

  const handleClose = () => {
    handleReset();
    onHide();
  };

  if (!patient) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Mark as DNA (Did Not Attend)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Patient Info */}
        <div className="patient-modal-info d-flex align-items-center gap-3 p-3 bg-light rounded mb-4">
          <div className="patient-modal-avatar">
            {getInitials(patient.patientName)}
          </div>
          <div>
            <h6 className="mb-0 fw-semibold">{patient.patientName}</h6>
            <small className="text-muted">{patient.nhsId}</small>
          </div>
        </div>

        {/* Warning Alert */}
        <Alert variant="danger" className="d-flex align-items-start gap-2">
          <AlertTriangle size={20} className="flex-shrink-0 mt-1" />
          <div>
            <strong>Confirm DNA Status</strong>
            <p className="mb-0 small">
              This will close the current consultation cycle for this patient and 
              mark them as "Did Not Attend" for this period.
            </p>
          </div>
        </Alert>

        {/* Reason Selection */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Reason for DNA <span style={{ color: 'red' }}>*</span></Form.Label>
          <Form.Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            <option value="">Select a reason...</option>
            <option value="no-show">Patient did not show up</option>
            <option value="cancelled">Patient cancelled</option>
            <option value="unreachable">Unable to contact patient</option>
            <option value="other">Other</option>
          </Form.Select>
        </Form.Group>

        {/* Additional Notes */}
        <Form.Group>
          <Form.Label className="fw-semibold">Additional Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Add any notes about the DNA..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleSubmit}
          disabled={submitting || !reason}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            <>
              <UserX size={16} className="me-1" />
              Confirm DNA
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DNAModal;